import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContract, getContract, updateContract } from '@/api/contracts';
import { ApiError } from '@/api/client';
import { contractSchema, contractTypes, type ContractFormData } from './schema';
import { SupplierPicker } from '@/components/SupplierPicker';
import type { ContractWrite } from '@/types/oscar';

const empty: ContractFormData = {
  ContractName: '', ContractNumber: '', Vendor: '', SupplierId: null, ContractType: 'HardwareLease',
  StartDate: '', EndDate: '', NoticeDate: '', MonthlyCost: null, AnnualCost: null, TotalValue: null, AutoRenews: false, Notes: '',
};
const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

export function ContractEditForm() {
  const { id } = useParams();
  const contractId = id ? Number(id) : null;
  const isEdit = contractId !== null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const contractQuery = useQuery({ queryKey: ['contract', contractId], queryFn: ({ signal }) => getContract(contractId!, signal), enabled: isEdit });
  const form = useForm<ContractFormData>({ resolver: zodResolver(contractSchema), defaultValues: empty });
  const { register, handleSubmit, reset, formState, watch, setValue } = form;

  useEffect(() => {
    if (contractQuery.data) {
      const c = contractQuery.data.Contract;
      reset({
        ContractName: c.ContractName, ContractNumber: c.ContractNumber ?? '', Vendor: c.Vendor, SupplierId: c.SupplierId ?? null, ContractType: c.ContractType,
        StartDate: c.StartDate?.slice(0, 10) ?? '', EndDate: c.EndDate?.slice(0, 10) ?? '', NoticeDate: c.NoticeDate?.slice(0, 10) ?? '',
        MonthlyCost: c.MonthlyCost, AnnualCost: c.AnnualCost, TotalValue: c.TotalValue, AutoRenews: c.AutoRenews, Notes: c.Notes ?? '',
      });
    }
  }, [contractQuery.data, reset]);

  function toPayload(d: ContractFormData): ContractWrite {
    const blank = (v?: string) => (v && v.trim() !== '' ? v.trim() : undefined);
    return {
      ContractName: d.ContractName.trim(), ContractNumber: blank(d.ContractNumber), Vendor: d.Vendor.trim(), SupplierId: d.SupplierId ?? undefined, ContractType: d.ContractType,
      StartDate: blank(d.StartDate), EndDate: blank(d.EndDate), NoticeDate: blank(d.NoticeDate),
      MonthlyCost: d.MonthlyCost ?? undefined, AnnualCost: d.AnnualCost ?? undefined, TotalValue: d.TotalValue ?? undefined,
      AutoRenews: d.AutoRenews, Notes: blank(d.Notes),
    };
  }

  const mutation = useMutation({
    mutationFn: (d: ContractFormData) => (isEdit ? updateContract(contractId!, toPayload(d)) : createContract(toPayload(d))),
    onSuccess: (saved) => { queryClient.invalidateQueries({ queryKey: ['contracts'] }); navigate(`/contracts/${saved.ContractId}`); },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not save the contract.'),
  });

  return (
    <section className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-navy">{isEdit ? 'Edit contract' : 'Add contract'}</h1>
      {submitError && <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{submitError}</div>}
      <form onSubmit={handleSubmit((d) => { setSubmitError(null); mutation.mutate(d); })} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-xs font-semibold text-darkGrey">Contract name<input {...register('ContractName')} className={inputCls} />{formState.errors.ContractName && <span className="mt-1 block font-normal text-red">{formState.errors.ContractName.message}</span>}</label>
          <label className="block text-xs font-semibold text-darkGrey">Vendor
            <SupplierPicker
              valueName={watch('Vendor')}
              valueId={watch('SupplierId') ?? null}
              onChange={(name, sid) => {
                setValue('Vendor', name, { shouldValidate: true, shouldDirty: true });
                setValue('SupplierId', sid, { shouldDirty: true });
              }}
              error={formState.errors.Vendor?.message}
              inputClassName={`${inputCls} mt-1`}
            />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Contract number<input {...register('ContractNumber')} className={inputCls} /></label>
          <label className="block text-xs font-semibold text-darkGrey">Type<select {...register('ContractType')} className={inputCls}>{contractTypes.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
          <label className="block text-xs font-semibold text-darkGrey">Start date<input type="date" {...register('StartDate')} className={inputCls} /></label>
          <label className="block text-xs font-semibold text-darkGrey">End date<input type="date" {...register('EndDate')} className={inputCls} /></label>
          <label className="block text-xs font-semibold text-darkGrey">Notice date<input type="date" {...register('NoticeDate')} className={inputCls} /></label>
          <label className="block text-xs font-semibold text-darkGrey">Monthly cost<input type="number" step="0.01" {...register('MonthlyCost', { setValueAs: (v) => (v === '' ? null : Number(v)) })} className={inputCls} /></label>
          <label className="block text-xs font-semibold text-darkGrey">Annual cost<input type="number" step="0.01" {...register('AnnualCost', { setValueAs: (v) => (v === '' ? null : Number(v)) })} className={inputCls} /></label>
          <label className="block text-xs font-semibold text-darkGrey">Total value<input type="number" step="0.01" {...register('TotalValue', { setValueAs: (v) => (v === '' ? null : Number(v)) })} className={inputCls} /></label>
          <label className="flex items-center gap-2 pt-5 text-xs font-semibold text-darkGrey"><input type="checkbox" {...register('AutoRenews')} /> Auto-renews</label>
        </div>
        <label className="block text-xs font-semibold text-darkGrey">Notes<textarea {...register('Notes')} rows={3} className={inputCls} /></label>
        <div className="flex gap-2">
          <button type="submit" disabled={mutation.isPending} className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">{mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create contract'}</button>
          <button type="button" onClick={() => navigate(isEdit ? `/contracts/${contractId}` : '/contracts')} className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg">Cancel</button>
        </div>
      </form>
    </section>
  );
}
